import { ChangeEvent, useEffect } from 'react'
import { Address, AddressWithId } from '../../../entities'
import useCities from '../../../services/queries/useCities'
import useStates from '../../../services/queries/useStates'
import { zipCodeFormat } from '../../../utils/format/zipCodeFormat'
import { Col } from '../Col'
import { Input } from '../Input'
import { Select } from '../Select'
import { Select as AntdSelect } from 'antd'
import { Dropdown } from '../Dropdown'
import {
  DeleteOutlined,
  DeliveredProcedureOutlined,
  EditOutlined,
  MoreOutlined
} from '@ant-design/icons'
import { MenuItem } from '../MenuItem'
import { Menu } from '../Menu'
import { colors } from '../../theme'

const { Option } = AntdSelect

type Props = {
  value: Address
  setValue(prop: any): void
  setFormState(prop: any): void
}

export const AddressForm = (props: Props) => {
  const { value, setValue, setFormState } = props

  const onSearch = (value: any) => {
    return value
  }
  const { data: states, isLoading: isStatesLoading } = useStates()
  const { data: cities, isLoading: isCitiesLoading } = useCities({
    page: 1,
    limit: 9999,
    filters: { state: value?.state }
  })

  const handleClear = (key: string) => {
    setValue((prev: Address) => ({ ...prev, [key]: '' }))
  }

  const handleStateClear = () => {
    setValue((prev: Address) => ({ ...prev, state: '', city: '' }))
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>, key: string) => {
    const {
      target: { value }
    } = event

    const parsedValue = typeof value === 'string' ? value.toUpperCase() : value

    setValue((prev: Address) => ({ ...prev, [key]: parsedValue }))
  }

  useEffect(() => {
    setValue((prev: Address) => ({
      ...prev,
      zipCode: zipCodeFormat(String(value.zipCode))
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.zipCode])

  useEffect(() => {
    if (
      value.state === '' ||
      value.city === '' ||
      value.district === '' ||
      value.address === '' ||
      value.number === ''
    ) {
      setFormState({ isError: true })
    } else {
      setFormState({ isError: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  return (
    <>
      <Col>
        <Select
          label="Estado"
          showSearch
          onSearch={onSearch}
          loading={isStatesLoading}
          allowClear
          onClear={handleStateClear}
          value={value.state || null}
          filterOption={(input, option) => {
            if (typeof option?.children === 'string') {
              return (
                option?.children?.toUpperCase().indexOf(input.toUpperCase()) >=
                0
              )
            }
            return false
          }}
          onSelect={(state: string) =>
            setValue((prev: Address) => ({ ...prev, state, city: '' }))
          }
        >
          {states?.data?.map((state) => (
            <Option key={state.id} value={state.id}>
              {`${state.id} - ${state.name}`}
            </Option>
          ))}
        </Select>
      </Col>
      <Col>
        <Select
          label="Munícipio"
          allowClear
          onClear={() => handleClear('city')}
          value={value.city || null}
          showSearch
          onSearch={onSearch}
          loading={isCitiesLoading}
          filterOption={(input, option) =>
            option?.children?.toUpperCase().indexOf(input.toUpperCase()) >= 0
          }
          onSelect={(city: string) =>
            setValue((prev: Address) => ({
              ...prev,
              city
            }))
          }
        >
          {cities?.data?.map((city) => (
            <Option key={city.id} value={city.id}>
              {city.name}
            </Option>
          ))}
        </Select>
      </Col>
      <Col>
        <Input
          label="Bairro"
          value={value.district}
          onChange={(e) => handleChange(e, 'district')}
        />
      </Col>
      <Col>
        <Input
          label="Endereço"
          value={value.address}
          onChange={(e) => handleChange(e, 'address')}
        />
      </Col>
      <Col>
        <Input
          label="Número"
          type="number"
          value={value.number}
          onChange={(e) => handleChange(e, 'number')}
        />
      </Col>
      <Col>
        <Input
          label="Complemento"
          value={value.complement}
          onChange={(e) => handleChange(e, 'complement')}
        />
      </Col>
      <Col>
        <Input
          label="CEP"
          value={value.zipCode}
          onChange={(e) => handleChange(e, 'zipCode')}
        />
      </Col>
    </>
  )
}

type AddressCardProps = {
  state: AddressWithId
  remove(id: string): void
  edit(id: string): void
  setMainAddress(id: string): void
}

export const AddressCard = (props: AddressCardProps) => {
  const { state, remove, edit, setMainAddress } = props
  const menu = () => {
    return (
      <Menu>
        <MenuItem onClick={() => edit(state.id)} icon={<EditOutlined />}>
          Editar
        </MenuItem>
        <MenuItem
          onClick={() => setMainAddress(state.id)}
          icon={<DeliveredProcedureOutlined />}
        >
          Definir como principal
        </MenuItem>
        <MenuItem onClick={() => remove(state.id)} icon={<DeleteOutlined />}>
          Deletar
        </MenuItem>
      </Menu>
    )
  }
  return (
    <div
      style={{
        background: 'white',
        borderRadius: 4,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between'
      }}
    >
      <div
        style={{
          padding: 10,
          width: '100%',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between'
          }}
        >
          <span
            style={{
              fontSize: 12,
              color: colors.textSecondary,
              marginBottom: 5
            }}
          >
            {state.type}
          </span>
          <Dropdown overlay={menu}>
            <MoreOutlined />
          </Dropdown>
        </div>
        <span>{`${state.address}, ${state.number} - ${state.district}, ${state.city}/${state.state} - ${state.zipCode}`}</span>
      </div>
    </div>
  )
}
